export type AmenityParentGroup = 'Unit' | 'Building' | 'Policies' | 'House' | 'Property';

export type AmenitySubGroup =
  | 'Kitchen'
  | 'Laundry'
  | 'Climate'
  | 'Interior features'
  | 'Connectivity'
  | 'Outdoor spaces'
  | 'Scenic views'
  | 'Parking'
  | 'Facilities'
  | 'Pet facilities'
  | 'Security'
  | 'Fitness and sports'
  | 'Social and work spaces'
  | 'Services'
  | 'Community type'
  | 'Special features'
  | 'Lease terms'
  | 'Pet policy'
  | 'Parking policy'
  | 'Utilities included'
  | 'Smoking'
  | 'Furnishing';

export type GroupedAmenity = { subGroup: AmenitySubGroup; items: Array<string> };
export type AmenityGroup = { parentGroup: AmenityParentGroup; subGroups: Array<GroupedAmenity> };

type SourceParentGroup = 'Unit' | 'Building' | 'Policies';
type AmenityMapping = { parentGroup: SourceParentGroup; subGroup: AmenitySubGroup };

const PARENT_GROUP_ORDER: Array<SourceParentGroup> = ['Unit', 'Building', 'Policies'];

const SUB_GROUP_ORDER: Record<SourceParentGroup, Array<AmenitySubGroup>> = {
  Unit: ['Kitchen', 'Laundry', 'Climate', 'Interior features', 'Connectivity', 'Outdoor spaces'],
  Building: [
    'Parking',
    'Facilities',
    'Pet facilities',
    'Security',
    'Fitness and sports',
    'Social and work spaces',
    'Outdoor spaces',
    'Scenic views',
    'Services',
    'Community type',
    'Special features',
  ],
  Policies: [
    'Lease terms',
    'Pet policy',
    'Parking policy',
    'Utilities included',
    'Smoking',
    'Furnishing',
    'Community type',
  ],
};

// Static mapping of amenity strings to their parent group and sub-group.
// Keys are stored in lowercase for case-insensitive matching.
// When multiple API variants exist for the same amenity (e.g. "BBQ grill"
// and "Barbecue"), each variant gets its own entry.
const AMENITY_MAP_RAW: Record<string, AmenityMapping> = {
  // Unit > Kitchen
  'convection oven': { parentGroup: 'Unit', subGroup: 'Kitchen' },
  dishwasher: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  'double oven': { parentGroup: 'Unit', subGroup: 'Kitchen' },
  freezer: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  'garbage disposal': { parentGroup: 'Unit', subGroup: 'Kitchen' },
  microwave: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  'microwave oven': { parentGroup: 'Unit', subGroup: 'Kitchen' },
  oven: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  range: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  refrigerator: { parentGroup: 'Unit', subGroup: 'Kitchen' },
  'trash compactor': { parentGroup: 'Unit', subGroup: 'Kitchen' },
  'kitchen island': { parentGroup: 'Unit', subGroup: 'Kitchen' },

  // Unit > Laundry
  dryer: { parentGroup: 'Unit', subGroup: 'Laundry' },
  washer: { parentGroup: 'Unit', subGroup: 'Laundry' },
  'washer/dryer hookups': { parentGroup: 'Unit', subGroup: 'Laundry' },

  // Unit > Climate
  'a/c': { parentGroup: 'Unit', subGroup: 'Climate' },
  'air conditioning': { parentGroup: 'Unit', subGroup: 'Climate' },
  'central a/c': { parentGroup: 'Unit', subGroup: 'Climate' },
  fan: { parentGroup: 'Unit', subGroup: 'Climate' },
  'ceiling fan': { parentGroup: 'Unit', subGroup: 'Climate' },
  'wall a/c': { parentGroup: 'Unit', subGroup: 'Climate' },
  'window a/c': { parentGroup: 'Unit', subGroup: 'Climate' },
  'electric heater': { parentGroup: 'Unit', subGroup: 'Climate' },
  'forced air': { parentGroup: 'Unit', subGroup: 'Climate' },
  'gas heater': { parentGroup: 'Unit', subGroup: 'Climate' },
  'heat pump': { parentGroup: 'Unit', subGroup: 'Climate' },
  'oil heater': { parentGroup: 'Unit', subGroup: 'Climate' },
  'propane/butane heater': { parentGroup: 'Unit', subGroup: 'Climate' },
  'solar heater': { parentGroup: 'Unit', subGroup: 'Climate' },

  // Unit > Interior features
  carpeted: { parentGroup: 'Unit', subGroup: 'Interior features' },
  'concrete floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  fireplace: { parentGroup: 'Unit', subGroup: 'Interior features' },
  'hardwood floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'laminate floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'linoleum floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'slate floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'softwood floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'tile floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'vinyl floor': { parentGroup: 'Unit', subGroup: 'Interior features' },
  'high ceilings': { parentGroup: 'Unit', subGroup: 'Interior features' },

  // Unit > Connectivity
  'building-wide wireless': { parentGroup: 'Unit', subGroup: 'Connectivity' },
  'cable tv': { parentGroup: 'Unit', subGroup: 'Connectivity' },
  'cable tv ready': { parentGroup: 'Unit', subGroup: 'Connectivity' },
  'high speed internet': { parentGroup: 'Unit', subGroup: 'Connectivity' },
  'high-speed internet ready': { parentGroup: 'Unit', subGroup: 'Connectivity' },
  'satellite tv': { parentGroup: 'Unit', subGroup: 'Connectivity' },

  // Unit > Outdoor spaces
  'patio / balcony': { parentGroup: 'Unit', subGroup: 'Outdoor spaces' },
  'patio balcony': { parentGroup: 'Unit', subGroup: 'Outdoor spaces' },
  balcony: { parentGroup: 'Unit', subGroup: 'Outdoor spaces' },

  // Building > Scenic views
  'city view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  'golf course view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  'mountain view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  'park view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  'water view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  'woodland view': { parentGroup: 'Building', subGroup: 'Scenic views' },
  description: { parentGroup: 'Building', subGroup: 'Scenic views' },

  // Building > Parking
  garage: { parentGroup: 'Building', subGroup: 'Parking' },
  'attached garage': { parentGroup: 'Building', subGroup: 'Parking' },
  'detached garage': { parentGroup: 'Building', subGroup: 'Parking' },
  'underground garage': { parentGroup: 'Building', subGroup: 'Parking' },
  'multi-level garage': { parentGroup: 'Building', subGroup: 'Parking' },
  'parking lot': { parentGroup: 'Building', subGroup: 'Parking' },
  'surface lot': { parentGroup: 'Building', subGroup: 'Parking' },
  'covered lot': { parentGroup: 'Building', subGroup: 'Parking' },
  'off street parking': { parentGroup: 'Building', subGroup: 'Parking' },
  'street parking': { parentGroup: 'Building', subGroup: 'Parking' },
  'electric vehicle charging stations': { parentGroup: 'Building', subGroup: 'Parking' },

  // Building > Facilities
  'bike storage': { parentGroup: 'Building', subGroup: 'Facilities' },
  'bicycle storage': { parentGroup: 'Building', subGroup: 'Facilities' },
  'disabled access': { parentGroup: 'Building', subGroup: 'Facilities' },
  'ada compliant apartments': { parentGroup: 'Building', subGroup: 'Facilities' },
  elevator: { parentGroup: 'Building', subGroup: 'Facilities' },
  'shared laundry': { parentGroup: 'Building', subGroup: 'Facilities' },
  storage: { parentGroup: 'Building', subGroup: 'Facilities' },

  // Building > Pet facilities
  'pet park': { parentGroup: 'Building', subGroup: 'Pet facilities' },
  'paw park': { parentGroup: 'Building', subGroup: 'Pet facilities' },
  'pet washing station': { parentGroup: 'Building', subGroup: 'Pet facilities' },
  'paw spa': { parentGroup: 'Building', subGroup: 'Pet facilities' },

  // Building > Security
  'controlled access': { parentGroup: 'Building', subGroup: 'Security' },
  doorman: { parentGroup: 'Building', subGroup: 'Security' },
  concierge: { parentGroup: 'Building', subGroup: 'Security' },
  'gated entry': { parentGroup: 'Building', subGroup: 'Security' },
  intercom: { parentGroup: 'Building', subGroup: 'Security' },
  'night patrol': { parentGroup: 'Building', subGroup: 'Security' },

  // Building > Fitness and sports
  'basketball court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'bocce court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'fitness center': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'hot tub': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'racquetball court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'shuffleboard court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  sauna: { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'sports court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'sports field': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'swimming pool': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'tennis court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'volleyball court': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'cold plunge': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'pilates and yoga studio': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'group excercise': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'group exercise': { parentGroup: 'Building', subGroup: 'Fitness and sports' },
  'digital fitness mirror': { parentGroup: 'Building', subGroup: 'Fitness and sports' },

  // Building > Social and work spaces
  'club house': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'game room': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  library: { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  lounge: { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'party room': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  theater: { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'business center': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'conference room': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'recreation room': { parentGroup: 'Building', subGroup: 'Social and work spaces' },
  'guest suite': { parentGroup: 'Building', subGroup: 'Social and work spaces' },

  // Building > Outdoor spaces
  'bbq grill': { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  barbecue: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  deck: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  garden: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  lawn: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  patio: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  'picnic area': { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  playground: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  'rooftop deck': { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  sundeck: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  trail: { parentGroup: 'Building', subGroup: 'Outdoor spaces' },
  'water feature': { parentGroup: 'Building', subGroup: 'Outdoor spaces' },

  // Building > Services
  '24 hour maintenance': { parentGroup: 'Building', subGroup: 'Services' },
  'dry cleaning drop off': { parentGroup: 'Building', subGroup: 'Services' },
  'on-site maintenance': { parentGroup: 'Building', subGroup: 'Services' },
  'on-site management': { parentGroup: 'Building', subGroup: 'Services' },
  'online maintenance portal': { parentGroup: 'Building', subGroup: 'Services' },
  'online rent payment': { parentGroup: 'Building', subGroup: 'Services' },
  'package service': { parentGroup: 'Building', subGroup: 'Services' },
  'spanish-speaking staff': { parentGroup: 'Building', subGroup: 'Services' },
  'valet trash': { parentGroup: 'Building', subGroup: 'Services' },

  // Building > Community type
  'assisted living': { parentGroup: 'Building', subGroup: 'Community type' },

  // Policies > Community type
  '55+ community': { parentGroup: 'Policies', subGroup: 'Community type' },

  // Policies > Lease terms
  flexible: { parentGroup: 'Policies', subGroup: 'Lease terms' },
  'less than 1 year': { parentGroup: 'Policies', subGroup: 'Lease terms' },
  monthly: { parentGroup: 'Policies', subGroup: 'Lease terms' },
  'more than 1 year': { parentGroup: 'Policies', subGroup: 'Lease terms' },
  'one year': { parentGroup: 'Policies', subGroup: 'Lease terms' },
  'six months': { parentGroup: 'Policies', subGroup: 'Lease terms' },

  // Policies > Pet policy
  'additional pet fees apply': { parentGroup: 'Policies', subGroup: 'Pet policy' },
  'number of dogs allowed': { parentGroup: 'Policies', subGroup: 'Pet policy' },
  'number of cats allowed': { parentGroup: 'Policies', subGroup: 'Pet policy' },
  'types of pets allowed': { parentGroup: 'Policies', subGroup: 'Pet policy' },

  // Policies > Parking policy
  'additional parking fees apply': { parentGroup: 'Policies', subGroup: 'Parking policy' },
  'assigned parking': { parentGroup: 'Policies', subGroup: 'Parking policy' },

  // Policies > Utilities included
  'cable tv included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'cooling included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'electricity included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'garbage included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'gas included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'heating included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'hot water included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'internet included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'internet incuded': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'sewer included': { parentGroup: 'Policies', subGroup: 'Utilities included' },
  'water included': { parentGroup: 'Policies', subGroup: 'Utilities included' },

  // Policies > Smoking
  'smoke free building': { parentGroup: 'Policies', subGroup: 'Smoking' },

  // Policies > Furnishing
  'furnished units available': { parentGroup: 'Policies', subGroup: 'Furnishing' },
};

// Regex patterns for amenities that can't be matched by exact name.
// Each entry: [pattern, mapping, optional transform]. Checked in order; first match wins.
// When a transform is provided, it cleans the display string (e.g. strips a prefix).
type PatternEntry = [RegExp, AmenityMapping, ((s: string) => string)?];

const PATTERN_MATCHERS: Array<PatternEntry> = [
  [/^\d+\s+spaces?$/i, { parentGroup: 'Policies', subGroup: 'Parking policy' }],
  // "Flooring: Tile" → Interior features, "Flooring: Hardwood" → Interior features
  [/^flooring:/i, { parentGroup: 'Unit', subGroup: 'Interior features' }],
  // "Laundry: In Unit" → strip prefix → "In Unit"
  [/^laundry:\s*/i, { parentGroup: 'Unit', subGroup: 'Laundry' }, (s) => s.replace(/^laundry:\s*/i, '')],
  // "Security: Controlled Access" → strip prefix → "Controlled Access"
  [/^security:\s*/i, { parentGroup: 'Building', subGroup: 'Security' }, (s) => s.replace(/^security:\s*/i, '')],
  // "View type: City view" → strip prefix → "City view"
  [/^view type:\s*/i, { parentGroup: 'Building', subGroup: 'Scenic views' }, (s) => s.replace(/^view type:\s*/i, '')],
  // Partial matches for pet facilities (e.g. "Paw Spa with Partnered Grooming Service")
  [/\bpaw spa\b/i, { parentGroup: 'Building', subGroup: 'Pet facilities' }],
  [/\bdog (park|run)\b/i, { parentGroup: 'Building', subGroup: 'Pet facilities' }],
  // Garage variants (e.g. "Garage Lot", "Covered Garage")
  [/\bgarage\b/i, { parentGroup: 'Building', subGroup: 'Parking' }],
];

const UNKNOWN_FALLBACK: AmenityMapping = { parentGroup: 'Building', subGroup: 'Special features' };

export function getCollapsedLabel(
  propertyType: string | undefined,
  listingType: string | undefined,
): 'House' | 'Property' | null {
  if (listingType === 'room') return 'Property';
  const pt = propertyType?.toLowerCase();
  if (pt === 'house' || pt === 'townhome') return 'House';
  return null;
}

export function mergeUnitAndBuildingGroups(
  groups: Array<AmenityGroup>,
  mergedLabel: 'House' | 'Property',
): Array<AmenityGroup> {
  const unitGroup = groups.find((g) => g.parentGroup === 'Unit');
  const buildingGroup = groups.find((g) => g.parentGroup === 'Building');
  const policiesGroup = groups.find((g) => g.parentGroup === 'Policies');

  const allSubGroups = [...(unitGroup?.subGroups ?? []), ...(buildingGroup?.subGroups ?? [])];

  // Consolidate duplicate sub-group names (e.g. "Outdoor spaces" appears in both Unit and Building)
  const seen = new Map<AmenitySubGroup, GroupedAmenity>();
  const mergedSubGroups: Array<GroupedAmenity> = [];
  for (const sg of allSubGroups) {
    const existing = seen.get(sg.subGroup);
    if (existing) {
      existing.items.push(...sg.items);
    } else {
      const entry = { subGroup: sg.subGroup, items: [...sg.items] };
      seen.set(sg.subGroup, entry);
      mergedSubGroups.push(entry);
    }
  }

  const result: Array<AmenityGroup> = [];
  if (mergedSubGroups.length > 0) {
    result.push({ parentGroup: mergedLabel, subGroups: mergedSubGroups });
  }
  if (policiesGroup) {
    result.push(policiesGroup);
  }
  return result;
}

export function groupAmenities(amenities: Array<string>): Array<AmenityGroup> {
  if (!Array.isArray(amenities)) return [];

  // Build intermediate structure: parentGroup -> subGroup -> items[]
  const grouped = new Map<SourceParentGroup, Map<AmenitySubGroup, Array<string>>>();

  for (const amenity of amenities) {
    if (typeof amenity !== 'string' || amenity.trim() === '') continue;

    const normalized = amenity.toLowerCase();

    // Step 1: Case-insensitive exact match
    let mapping: AmenityMapping | undefined = AMENITY_MAP_RAW[normalized];

    // Step 2: Pattern match (only if exact match failed)
    let displayName = amenity;
    if (!mapping) {
      for (const [pattern, patternMapping, transform] of PATTERN_MATCHERS) {
        if (pattern.test(amenity)) {
          mapping = patternMapping;
          if (transform) displayName = transform(amenity);
          break;
        }
      }
    }

    // Step 3: Unknown fallback to Building > Special features
    if (!mapping) {
      mapping = UNKNOWN_FALLBACK;
    }

    if (!grouped.has(mapping.parentGroup)) {
      grouped.set(mapping.parentGroup, new Map());
    }
    const subGroups = grouped.get(mapping.parentGroup)!;
    if (!subGroups.has(mapping.subGroup)) {
      subGroups.set(mapping.subGroup, []);
    }
    subGroups.get(mapping.subGroup)!.push(displayName);
  }

  // Convert to output format with fixed ordering
  const result: Array<AmenityGroup> = [];

  for (const parentGroup of PARENT_GROUP_ORDER) {
    const subGroupMap = grouped.get(parentGroup);
    if (!subGroupMap) continue;

    const orderedSubGroups: Array<GroupedAmenity> = [];
    for (const subGroup of SUB_GROUP_ORDER[parentGroup]) {
      const items = subGroupMap.get(subGroup);
      if (items && items.length > 0) {
        orderedSubGroups.push({ subGroup, items });
      }
    }

    if (orderedSubGroups.length > 0) {
      result.push({ parentGroup, subGroups: orderedSubGroups });
    }
  }

  return result;
}
